<template lang="pug">
    tr

        // User Full Name
        td {{ item.fullName }}

        // User Email
        td {{ item.email }}

        // Business Name
        td(v-if="isAdmin")
            span(v-if="item.business && item.business.name") {{ item.business.name }}

        // Phone
        td(v-if="item.phone && isChampion") {{ item.phone }}

        // Trial period
        td(v-if="isAdmin")
            div(v-if="item.subscription && item.statusValue === 'trial'")
                div.periodLabel stripe
                span {{ item.createdAt | formattedDate }}
                span {{ ` - ` }}
                span {{ item.subscription.trialPeriodEndedAt | formattedDate }}

            div(v-if="!item.subscription && item.availableUntil && item.statusValue === 'trial'")
                div.periodLabel default
                span {{ item.createdAt | formattedDate }}
                span {{ ` - ` }}
                span {{ item.availableUntil | formattedDate }}

        // Status
        td
            user-status(:status="item.statusValue")

        // Coupon used
        td(v-if="isAdmin") {{ item.subscription ? item.subscription.coupon : '' }}

        // Device
        td(v-if="isAdmin")
            span.deviceBadge(
                v-for="platform in devicePlatforms"
                :key="platform"
                :class="`deviceBadge--${platform.toLowerCase()}`"
            ) {{ platform }}

        // City
        td(v-if="isChampion") {{ item.city }}

        // State
        td(v-if="isChampion") {{ item.state }}

        // Country
        td(v-if="isChampion") {{ item.country }}

        // Department
        td(v-if="isChampion && showDepartment") {{ item.department }}

        // Actions
        td.actions.text-xs-right(class="justify-center px-0 actions")

            // User statistics button
            button-component.statisticsView(:action="statisticsView" color="primary" icon="insert_chart")

            // Send push notification button (admin only, requires registered device)
            button-component.sendPush(
                v-if="isAdmin && hasDevice"
                :action="openPushModal"
                color="primary"
                icon="notifications"
            )

            // Set a new password for this user (admin only)
            button-component.setPassword(
                v-if="isAdmin"
                :action="openPasswordModal"
                color="primary"
                icon="vpn_key"
            )

            // Edit user button
            button-component.edit(:action="edit" color="primary" icon="edit")

            // Delete user button
            button-component.delete(:action="remove" color="primary" icon="delete")

            button-component.edit(:action="change" color="primary" icon="repeat")

            push-notification-modal(
                v-if="isAdmin && hasDevice"
                v-model="isPushModalOpen"
                :userId="item.id"
            )

            set-password-modal(
                v-if="isAdmin"
                v-model="isPasswordModalOpen"
                :userId="item.id"
            )
</template>

<script>
    import VueTypes from 'vue-types';
    import UserStatus from './UserStatus';
    import PushNotificationModal from './PushNotificationModal';
    import SetPasswordModal from './SetPasswordModal';
    import ButtonIcon from '~components/ButtonIcon';
    import formattedDate from '~filters/FormattedDate';
    import { mapActions, mapGetters } from 'vuex';

    export default {
        components: {
            UserStatus,
            PushNotificationModal,
            SetPasswordModal,
            ButtonComponent: ButtonIcon
        },
        filters: {
            formattedDate
        },
        props: {
            item: VueTypes.object.def({})
        },
        data() {
            return {
                isPushModalOpen: false,
                isPasswordModalOpen: false
            };
        },
        computed: {
            ...mapGetters('auth', ['isChampion', 'isAdmin', 'showDepartment']),
            devicePlatforms() {
                const devices = this.item.devices || [];
                const platforms = devices
                    .filter(device => device && device.id && device.platform)
                    .map(device => {
                        const value = String(device.platform).toLowerCase();
                        if (value === 'ios') return 'iOS';
                        return value.charAt(0).toUpperCase() + value.slice(1);
                    });

                return [...new Set(platforms)];
            },
            hasDevice() {
                const devices = this.item.devices || [];
                return devices.some(device => device && device.id);
            }
        },
        methods: {
            ...mapActions('users', ['deleteAccount', 'changeBusiness']),

            openPushModal() {
                this.isPushModalOpen = true;
            },

            openPasswordModal() {
                this.isPasswordModalOpen = true;
            },

            /**
             * Edit business
             */
            edit() {
                this.$router.push({
                    name: 'dashboard.users.edit',
                    params: { id: this.item.id }
                });
            },
            /**
             * Edit business
             */
            statisticsView() {
                this.$router.push({
                    name: 'dashboard.statistics.by.user',
                    params: { id: this.item.id }
                });
            },

            /**
             * Delete business.
             * @returns {Promise<void>}
             */
            async remove() {
                const resp = await this.deleteAccount(this.item.id);

                if (resp) {
                    if (
                        this.$router.currentRoute.name ===
                        'dashboard.users.by.business'
                    ) {
                        this.$router.go(this.$router.currentRoute);
                    } else {
                        this.$emit('removed', this.item.id);
                    }
                }
            },

            /**
             * Change business.
             * @returns {Promise<void>}
             */
            async change() {
                const resp = await this.changeBusiness(this.item.id);

                if (resp) {
                    if (
                        this.$router.currentRoute.name ===
                        'dashboard.users.by.business'
                    ) {
                        this.$router.go(this.$router.currentRoute);
                    } else {
                        this.$emit('removed', this.item.id);
                    }
                }
            }
        }
    };
</script>

<style lang="stylus" scoped>
    .actions
        min-width: 115px

    .periodLabel
        text-transform: uppercase
        font-size: 10px

    .deviceBadge
        display: inline-block
        padding: 2px 8px
        margin-right: 4px
        border-radius: 10px
        font-size: 11px
        font-weight: 500
        background-color: #e0e0e0
        color: #424242

        &--ios
            background-color: #e3f2fd
            color: #1565c0

        &--android
            background-color: #e8f5e9
            color: #2e7d32
</style>
